import { useState, useCallback } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import { GET_CART, GET_CLOSEST_STORES_FROM_ZIPCODE } from 'mocks/queries';
import { ZIP_CODE_REGEX, ZIPCODE_ERROR_MESSAGES } from 'commons/constants';
import { logError } from 'commons/logger';
import useSetZipCodeFromUser from 'global-state/zipCodeFromUser/useSetZipCodeFromUser';

const EMPTY_STORES = [];

export const validateZipcodeInput = inputs => {
  if (!inputs.zipcode.toString().trim()) {
    return { zipcode: ZIPCODE_ERROR_MESSAGES.ZIPCODE_IS_EMPTY };
  }

  if (!ZIP_CODE_REGEX.test(inputs.zipcode)) {
    return { zipcode: ZIPCODE_ERROR_MESSAGES.ZIPCODE_IS_INVALID_FORMAT };
  }

  return {};
};

const useShowroomLocator = ({ limit = 10 } = {}) => {
  const apolloClient = useApolloClient();
  const [searchZipcode, setSearchZipcode] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const setZipCodeFromUser = useSetZipCodeFromUser();

  const { data, loading, error } = useQuery(GET_CLOSEST_STORES_FROM_ZIPCODE, {
    variables: { zipcode: searchZipcode, limit },
    skip: !hasSearched || !searchZipcode,
    errorPolicy: 'all',
  });

  const stores = data?.getClosestStoresFromZipcode || EMPTY_STORES;

  const handleSearch = useCallback(
    zipcode => {
      const trimmedZipCode = zipcode.trim();
      setSearchZipcode(trimmedZipCode);
      setHasSearched(true);
      setZipCodeFromUser(trimmedZipCode);
      apolloClient
        .refetchQueries({
          include: [GET_CART],
        })
        .catch(refetchError => {
          logError(
            refetchError,
            'Failed to refetch GET_CART after showroom zip search'
          );
        });
    },
    [apolloClient, setZipCodeFromUser]
  );

  const handleCloseResults = useCallback(() => {
    setHasSearched(false);
  }, []);

  const handleViewAll = useCallback(() => {
    setSearchZipcode('');
    setHasSearched(false);
  }, []);

  return {
    stores,
    loading,
    error,
    hasSearched,
    handleSearch,
    handleCloseResults,
    handleViewAll,
  };
};

export default useShowroomLocator;
